import { ApiError } from '../../services/api';

/**
 * Spec 074 (#1145) — shared labels for the terms status (list column, detail card) and the
 * terms-notice sends. Every badge carries a text label, so colour is never the only signal.
 */

/** The screen that grants both sends (identity V24; SUPER_ADMIN always has it). */
export const TERMS_NOTICE_SEND_SCREEN = 'signed-agreements';

/**
 * v2 (B1): two states for the admin. NOTIFIED (the terms email was delivered) reads "Signed"; `detail`
 * says how, as a tooltip in lists and as text on the garage page.
 */
export const TERMS_STATUS_BADGE: Record<string, { label: string; cls: string; detail?: string }> = {
  SIGNED: { label: 'Signed', cls: 'approved' },
  NOTIFIED: { label: 'Signed', cls: 'approved', detail: 'via email notice' },
  NOT_SIGNED: { label: 'Not signed', cls: 'warning' },
};

export function termsStatusBadge(status: string): { label: string; cls: string; detail?: string } {
  return TERMS_STATUS_BADGE[status] ?? { label: status, cls: 'warning' };
}

/** "Signed" covers both a real signature and a delivered email notice (the backend SIGNED filter does too). */
export function isSignedStatus(status: string | null | undefined): boolean {
  return status === 'SIGNED' || status === 'NOTIFIED';
}

/** The Terms dropdown on the Garages page; values mirror the `terms` URL param. */
export const TERMS_FILTER_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All terms' },
  { value: 'SIGNED', label: 'Signed' },
  { value: 'NOT_SIGNED', label: 'Not signed' },
];

const RECIPIENT_STATUS: Record<string, { label: string; cls: string }> = {
  PENDING: { label: 'Queued', cls: 'pending' },
  REQUESTED: { label: 'Requested', cls: 'pending' },
  SENT: { label: 'Sent', cls: 'pending' },
  DELAYED: { label: 'Delayed', cls: 'pending' },
  DELIVERED: { label: 'Delivered', cls: 'approved' },
  BOUNCED: { label: 'Bounced', cls: 'rejected' },
  COMPLAINED: { label: 'Marked as spam', cls: 'rejected' },
  SUPPRESSED: { label: 'Suppressed', cls: 'rejected' },
  FAILED: { label: 'Failed', cls: 'rejected' },
};

export function recipientStatusBadge(status: string): { label: string; cls: string } {
  return RECIPIENT_STATUS[status] ?? { label: status, cls: 'pending' };
}

export const RECIPIENT_KIND_LABEL: Record<string, string> = {
  GARAGE: 'Garage email',
  OWNER: 'Owner',
};

const ERROR_MESSAGE: Record<string, string> = {
  TERMS_NOTICE_IN_FLIGHT: 'A notice is already on its way. Wait for the delivery result before sending again.',
  TERMS_NOTICE_NO_RECIPIENT: 'No email address on file for this garage or its owner.',
  TERMS_NOTICES_DISABLED: 'Terms notices are switched off in Runtime Config (garagat.terms.notices.enabled).',
  TERMS_NOTICE_NO_DOCUMENTS: 'There are no active partner terms documents to send.',
  TERMS_NOTICE_GARAGE_DRAFT: "This garage hasn't finished registering, so it can't be notified yet.",
  TERMS_NOTICE_INVALID_SELECTION: 'Select between 1 and 100 garages per send.',
};

/** Why a garage was left out of a send (`skipped[].reason`). */
export const SKIP_REASON: Record<string, string> = {
  IN_FLIGHT: 'A notice is already on its way',
  NO_RECIPIENT: 'No email address on file',
  NOT_ELIGIBLE: 'Not eligible (draft, rejected or no longer listed)',
};

export function skipReasonLabel(reason: string): string {
  return SKIP_REASON[reason] ?? reason;
}

/** One line for any send or load failure. */
export function termsNoticeErrorMessage(err: unknown): string {
  if (err instanceof ApiError) {
    if (err.code && ERROR_MESSAGE[err.code]) return ERROR_MESSAGE[err.code];
    if (err.status === 403) return "You don't have permission to send terms notices.";
  }
  return err instanceof Error && err.message ? err.message : 'Something went wrong. Try again.';
}

/** UAE time, like the Signed agreements card. */
export function formatUae(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return `${new Date(iso).toLocaleString('en-GB', {
      timeZone: 'Asia/Dubai',
      year: 'numeric',
      month: 'short',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })} GST`;
  } catch {
    return iso;
  }
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}
